import { apiRequest } from './apiRequest'
import { getPreferredUserIdentifier } from '../utils/userIdentity'

// User Info Types
export interface UserInfoData {
    _id: string
    userId: string
    username: string
    mobile: string
    tenantId: string
    isSystemUser: boolean
    name: string
    email: string
    vendorId: string | null
    parentUserId: string | null
    referrerUsername: string
    siteName: string
    walletBalanceSnapshot: number
    lastIpAddress: string | null
    lastLoginAt: string | null
    lastLogoutAt: string | null
    failedLoginAttempts: number
    lockUntil: string | null
    bankAccount: BankAccount[]
    trueWallet: any[]
    lineAccountId: string
    isAutoDeposit: boolean
    isAutoWithdraw: boolean
    isActiveAccount: boolean
    isEnableChat: boolean
    isShow: boolean
    registerAt: string
    status: 'online' | 'offline'
    bettingLimitGroupId: string | null
    bettingLimitGroupName: string | null
    serviceConfigs: any[]
    limitConfigs: any[]
    promotions: any[]
    bonuses: any[]
    flagColor: string
    avatar: string | null
    timezone: string
    language: string
    notes: any[]
    createdAt: string
    updatedAt: string
}

export interface BankAccount {
    bankCode: string
    bankName: string
    accountNumber: string
    accountName: string
    promptPayId?: string
    isActive: boolean
    isDelete: boolean
    createdAt: string
    updatedAt: string
}

export interface UserInfoResponse {
    success: boolean
    data?: UserInfoData
    error?: string
}

export interface UpdateUserInfoRequest {
    name?: string
    email?: string
    mobile?: string
    timezone?: string
    language?: string
    isAutoDeposit?: boolean
    isAutoWithdraw?: boolean
    isEnableChat?: boolean
    bankAccount?: Partial<BankAccount>[]
}

export const userInfoService = {
    // ดึงข้อมูล user info
    async getUserInfo(userId?: string): Promise<UserInfoResponse> {
        try {
            const user = localStorage.getItem('user')
            if (!user) {
                return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
            }

            const userData = JSON.parse(user)
            const targetUserId = userId || getPreferredUserIdentifier(userData)
            const tenantId = userData.tenantId

            if (!targetUserId || !tenantId) {
                return { success: false, error: 'ไม่พบ User ID หรือ Tenant ID' }
            }

            // Use user authentication instead of service token
            const response = await apiRequest<UserInfoResponse>(`/api/v1/user-info/${tenantId}/${targetUserId}`)

            return response
        } catch (error: any) {
            console.error('❌ Get user info failed:', error)
            return {
                success: false,
                error: error.response?.data?.error || 'ไม่สามารถดึงข้อมูล user info ได้'
            }
        }
    },

    // อัปเดตข้อมูล user info
    async updateUserInfo(updateData: UpdateUserInfoRequest): Promise<UserInfoResponse> {
        try {
            const user = localStorage.getItem('user')
            if (!user) {
                return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
            }

            const userData = JSON.parse(user)
            const userId = getPreferredUserIdentifier(userData)
            const tenantId = userData.tenantId

            if (!userId || !tenantId) {
                return { success: false, error: 'ไม่พบ User ID หรือ Tenant ID' }
            }

            // Use user-facing endpoint (Bearer token) — same as user-modern
            const response = await apiRequest<UserInfoResponse>(
                `/api/v1/user-info/${tenantId}/${userId}`,
                {
                    method: 'PUT',
                    body: updateData
                }
            )

            return response
        } catch (error: any) {
            console.error('❌ Update user info failed:', error)
            return {
                success: false,
                error: error.response?.data?.error || 'ไม่สามารถอัปเดตข้อมูล user info ได้'
            }
        }
    },

    // ตั้งค่า PromptPay ID ลงบัญชีธนาคารหลัก (เซ็ตได้ครั้งเดียว หลังจากนั้นต้องให้แอดมินแก้)
    async setBankPromptPay(promptPayId: string): Promise<UserInfoResponse> {
        try {
            const user = localStorage.getItem('user')
            if (!user) {
                return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
            }

            const userData = JSON.parse(user)
            const userId = getPreferredUserIdentifier(userData)
            const tenantId = userData.tenantId

            if (!userId || !tenantId) {
                return { success: false, error: 'ไม่พบ User ID หรือ Tenant ID' }
            }

            const normalized = String(promptPayId || '').replace(/\D/g, '')
            if (!/^(\d{10}|\d{13})$/.test(normalized)) {
                return { success: false, error: 'PromptPay ต้องเป็นเบอร์มือถือ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก' }
            }

            const response = await apiRequest<UserInfoResponse>(
                `/api/v1/user-info/${tenantId}/${userId}/bank/promptpay`,
                {
                    method: 'PUT',
                    body: { promptPayId: normalized }
                }
            )

            return response
        } catch (error: any) {
            console.error('❌ Set PromptPay failed:', error)
            return {
                success: false,
                error: error.response?.data?.error || 'ไม่สามารถบันทึก PromptPay ได้'
            }
        }
    },

    // ดึงข้อมูลธนาคาร
    getBankAccounts(userInfo: UserInfoData | null): BankAccount[] {
        if (!userInfo || !userInfo.bankAccount) {
            return []
        }
        return userInfo.bankAccount.filter(account => !account.isDelete)
    },

    // ดึงข้อมูลธนาคารหลัก
    getPrimaryBankAccount(userInfo: UserInfoData | null): BankAccount | null {
        const accounts = this.getBankAccounts(userInfo)
        if (accounts.length === 0) {
            return null
        }

        // Find active bank account
        const activeAccount = accounts.find(account => account.isActive)
        return activeAccount || accounts[0]
    },

    // ฟอร์แมตหมายเลขบัญชี (ซ่อนบางส่วน)
    formatAccountNumber(accountNumber: string): string {
        if (!accountNumber || accountNumber.length < 4) {
            return accountNumber
        }

        const visibleLength = 4
        const maskedLength = accountNumber.length - visibleLength
        const visiblePart = accountNumber.slice(-visibleLength)
        const maskedPart = '*'.repeat(maskedLength)

        return `${maskedPart}${visiblePart}`
    },

    // ฟอร์แมตชื่อธนาคาร
    formatBankName(bankCode: string): string {
        const bankNames: { [key: string]: string } = {
            'BAAC': 'ธนาคาร ธกส.',
            'BAY': 'ธนาคารกรุงศรีอยุธยา',
            'BBL': 'ธนาคารกรุงเทพ',
            'GSB': 'ธนาคารออมสิน',
            'KBANK': 'ธนาคารกสิกรไทย',
            'KTB': 'ธนาคารกรุงไทย',
            'SCB': 'ธนาคารไทยพาณิชย์',
            'TTB': 'ธนาคารทหารไทยธนชาต'
        }

        return bankNames[bankCode] || bankCode
    },

    // ตรวจสอบสถานะการยืนยันตัวตน
    getVerificationStatus(userInfo: UserInfoData | null): {
        level: 'basic' | 'intermediate' | 'advanced'
        status: 'pending' | 'verified' | 'rejected'
        message: string
    } {
        if (!userInfo) {
            return {
                level: 'basic',
                status: 'pending',
                message: 'ยังไม่ได้ยืนยันตัวตน'
            }
        }

        // Simple verification logic based on available data
        if (userInfo.bankAccount && userInfo.bankAccount.length > 0) {
            return {
                level: 'intermediate',
                status: 'verified',
                message: 'ยืนยันตัวตนแล้ว'
            }
        }

        return {
            level: 'basic',
            status: 'pending',
            message: 'ยังไม่ได้ยืนยันตัวตน'
        }
    }
}
